import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  TextField,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip
} from '@material-ui/core';
import {
  PictureAsPdf,
  CloudUpload,
  Category,
  Close
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import { getActiveTags, createTag } from '../../services/tagService_new';
import { uploadFile } from '../../services/firebaseStorage';
import './style.scss';

const EBookUploadForm = ({ open, onClose, editingEBook, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    requiredRank: '',
    author: '',
    pages: 1,
    language: 'ro',
    tags: [],
    isActive: true,
    order: 0
  });

  const [ebookFile, setEbookFile] = useState(null);
  const [ebookUrl, setEbookUrl] = useState('');
  const [ebookProgress, setEbookProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');

  const fileInputRef = useRef(null);

  const ranks = [
    { value: 'basic', label: 'Basic' },
    { value: 'team_member', label: 'Team Member' },
    { value: 'premier', label: 'Premier' },
    { value: 'silver', label: 'Silver' },
    { value: 'gold', label: 'Gold' },
    { value: 'platinum', label: 'Platinum' },
    { value: 'diamond', label: 'Diamond' },
    { value: 'blue_diamond', label: 'Blue Diamond' },
    { value: 'presidential_diamond', label: 'Presidential Diamond' }
  ];

  const categories = [
    'Educație',
    'Ghiduri practice',
    'Manuale',
    'Protocoale',
    'Rețete',
    'Cercetare',
    'Business',
    'Marketing'
  ];

  const languages = [
    { value: 'ro', label: 'Română' },
    { value: 'en', label: 'Engleză' },
    { value: 'fr', label: 'Franceză' },
    { value: 'de', label: 'Germană' }
  ];

  useEffect(() => {
    if (open) {
      loadTags();
      if (editingEBook) {
        setFormData({
          title: editingEBook.title || '',
          description: editingEBook.description || '',
          category: editingEBook.category || '',
          requiredRank: editingEBook.requiredRank || '',
          author: editingEBook.author || '',
          pages: editingEBook.pages || 1,
          language: editingEBook.language || 'ro',
          tags: editingEBook.tags || [],
          isActive: editingEBook.isActive !== undefined ? editingEBook.isActive : true,
          order: editingEBook.order || 0
        });
        setEbookUrl(editingEBook.ebookUrl || '');
      } else {
        resetForm();
      }
    }
  }, [open, editingEBook]);

  const loadTags = async () => {
    try {
      console.log('Loading tags...');
      const tags = await getActiveTags();
      console.log('Tags received:', tags);
      
      if (Array.isArray(tags)) {
        setAvailableTags(tags);
        console.log('Tags set successfully:', tags.length, 'tags');
      } else {
        console.warn('Tags response is not an array:', tags);
        setAvailableTags([]);
      }
    } catch (error) {
      console.error('Error loading tags:', error);
      setAvailableTags([]);
      toast.error('Eroare la încărcarea tag-urilor. Se va folosi lista goală.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      requiredRank: '',
      author: '',
      pages: 1,
      language: 'ro',
      tags: [],
      isActive: true,
      order: 0
    });
    setEbookFile(null);
    setEbookUrl('');
    setEbookProgress(0);
    setNewTagName('');
  };

  const handleEbookSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setEbookFile(file);
    } else {
      toast.error('Te rugăm să selectezi un fișier PDF valid');
    }
  };

  const uploadFileWithProgress = (file, folder) => {
    return new Promise((resolve, reject) => {
      uploadFile(file, folder, 
        (progress) => {
          setEbookProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        (downloadURL) => {
          resolve(downloadURL);
        }
      );
    });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Titlul este obligatoriu');
      return;
    }

    if (!ebookFile && !ebookUrl) {
      toast.error('Te rugăm să încărci un eBook');
      return;
    }

    setUploading(true);

    try {
      let finalEbookUrl = ebookUrl;

      if (ebookFile) {
        toast.info('Se încarcă eBook-ul...');
        finalEbookUrl = await uploadFileWithProgress(ebookFile, 'ebooks');
      }

      const ebookData = {
        ...formData,
        ebookUrl: finalEbookUrl,
        id: editingEBook?.id || `ebook_${Date.now()}`
      };

      await onSave(ebookData);
      toast.success(editingEBook ? 'eBook actualizat cu succes!' : 'eBook adăugat cu succes!');
      onClose();
    } catch (error) {
      console.error('Error saving ebook:', error);
      toast.error('Eroare la salvarea eBook-ului');
    } finally {
      setUploading(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTagName?.trim()) {
      toast.warning('Introduceți numele pentru tag');
      return;
    }

    try {
      console.log('Creating new tag:', newTagName.trim());
      const newTag = await createTag({
        name: newTagName.trim(),
        category: 'ebook',
        isActive: true
      });

      if (newTag && newTag.id) {
        setAvailableTags(prev => [...prev, newTag]);
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag.name]
        }));
        setNewTagName('');
        toast.success('Tag adăugat cu succes!');
      } else {
        throw new Error('Tag creation returned invalid response');
      }
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error('Eroare la adăugarea tag-ului: ' + (error.message || 'Eroare necunoscută'));
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      className="ebook-upload-dialog"
    >
      <div className="glass-dialog-container">
        <div className="glass-dialog-header">
          <div className="header-icon-wrapper">
            <PictureAsPdf className="header-icon" />
          </div>
          <div className="header-content">
            <h2 className="dialog-title">
              {editingEBook ? 'Editează eBook' : 'Adaugă eBook Nou'}
            </h2>
            <p className="dialog-subtitle">
              Încarcă și configurează eBook-ul pentru biblioteca ta
            </p>
          </div>
          <button 
            className="close-button-modern" 
            onClick={onClose}
          >
            <Close />
          </button>
        </div>

        <div className="glass-dialog-content">
          <div className="form-sections">
            {/* Basic Information */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card className="glass-card">
                  <CardContent>
                    <Typography variant="h6" className="section-title">
                      <Category className="section-icon" />
                      Informații de Bază
                    </Typography>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <TextField
                          fullWidth
                          label="Titlu *"
                          value={formData.title}
                          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                          className="glass-input"
                          disabled={uploading}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Descriere"
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          className="glass-input"
                          disabled={uploading}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <select
                          className="glass-select"
                          value={formData.category}
                          onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                          disabled={uploading}
                        >
                          <option value="">Selectează categoria</option>
                          {categories.map(category => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <select
                          className="glass-select"
                          value={formData.requiredRank}
                          onChange={(e) => setFormData(prev => ({ ...prev, requiredRank: e.target.value }))}
                          disabled={uploading}
                        >
                          <option value="">Selectează rangul</option>
                          {ranks.map(rank => (
                            <option key={rank.value} value={rank.value}>
                              {rank.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <TextField
                          fullWidth
                          label="Autor"
                          value={formData.author}
                          onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                          className="glass-input"
                          disabled={uploading}
                        />
                      </div>
                      <div className="form-group">
                        <TextField
                          fullWidth
                          type="number"
                          label="Numărul de pagini"
                          value={formData.pages}
                          onChange={(e) => setFormData(prev => ({ ...prev, pages: parseInt(e.target.value) || 1 }))}
                          className="glass-input"
                          disabled={uploading}
                          inputProps={{ min: 1 }}
                        />
                      </div>
                      <div className="form-group">
                        <select
                          className="glass-select"
                          value={formData.language}
                          onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                          disabled={uploading}
                        >
                          {languages.map(lang => (
                            <option key={lang.value} value={lang.value}>
                              {lang.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* eBook Upload */}
              <Grid item xs={12}>
                <Card className="glass-card">
                  <CardContent>
                    <Typography variant="h6" className="section-title">
                      <PictureAsPdf className="section-icon" />
                      eBook PDF
                    </Typography>
                    
                    <div className="upload-section">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleEbookSelect}
                        style={{ display: 'none' }}
                        ref={fileInputRef}
                        disabled={uploading}
                      />
                      
                      <Button
                        variant="outlined"
                        startIcon={<CloudUpload />}
                        onClick={() => fileInputRef.current.click()}
                        className="upload-button glass-button"
                        disabled={uploading}
                      >
                        Încarcă eBook PDF
                      </Button>
                      
                      {ebookFile && (
                        <div className="file-info">
                          <Typography variant="body2">
                            eBook selectat: {ebookFile.name}
                          </Typography>
                        </div>
                      )}
                      
                      {ebookProgress > 0 && ebookProgress < 100 && (
                        <div className="upload-progress">
                          <Typography variant="body2">
                            Încărcare eBook: {Math.round(ebookProgress)}%
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={ebookProgress} 
                            className="glass-progress"
                          />
                        </div>
                      )}
                      
                      {ebookUrl && (
                        <div className="preview-section">
                          <Typography variant="body2" className="preview-label">
                            <PictureAsPdf /> eBook încărcat cu succes
                          </Typography>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* Tags */}
              <Grid item xs={12}>
                <Card className="glass-card">
                  <CardContent>
                    <Typography variant="h6" className="section-title">
                      Tag-uri
                    </Typography>
                    
                    <div className="tags-section">
                      <div className="available-tags">
                        <Typography variant="subtitle2" gutterBottom>
                          Tag-uri disponibile:
                        </Typography>
                        <div className="tags-grid">
                          {availableTags.map((tag) => (
                            <Chip
                              key={tag.id}
                              label={tag.name}
                              clickable
                              onClick={() => {
                                if (!formData.tags.includes(tag.name)) {
                                  setFormData(prev => ({
                                    ...prev,
                                    tags: [...prev.tags, tag.name]
                                  }));
                                }
                              }}
                              className={formData.tags.includes(tag.name) ? 'tag-selected' : 'tag-available'}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="selected-tags">
                        <Typography variant="subtitle2" gutterBottom>
                          Tag-uri selectate:
                        </Typography>
                        <div className="tags-grid">
                          {formData.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              onDelete={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  tags: prev.tags.filter(t => t !== tag)
                                }));
                              }}
                              className="tag-selected"
                            />
                          ))}
                        </div>
                      </div>

                      <div className="add-tag-section">
                        <div className="add-tag-input">
                          <TextField
                            size="small"
                            label="Adaugă tag nou"
                            value={newTagName}
                            onChange={(e) => setNewTagName(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddTag();
                              }
                            }}
                            disabled={uploading}
                          />
                          <Button
                            onClick={handleAddTag}
                            disabled={!newTagName.trim() || uploading}
                            className="add-tag-btn"
                          >
                            Adaugă
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </div>
        </div>

        <div className="glass-dialog-footer">
          <button 
            onClick={onClose} 
            disabled={uploading}
            className="cancel-button-modern"
          >
            Anulează
          </button>
          <button 
            onClick={handleSubmit} 
            disabled={uploading || (!ebookFile && !ebookUrl) || !formData.title.trim()}
            className="save-button-modern"
          >
            {uploading ? 'Se salvează...' : (editingEBook ? 'Actualizează' : 'Salvează')}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default EBookUploadForm;
